import React, { useState, useRef, useEffect } from "react";

interface EditableProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableDiv: React.FC<EditableProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [value, setValue] = useState(text);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.innerText;
    setValue(newValue);
    onEdit(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      divRef.current?.blur();
    }
  };

  return (
    <div
      ref={divRef}
      contentEditable
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`cursor-text outline-none ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
      suppressContentEditableWarning
    >
      {value || placeholder}
    </div>
  );
};

interface SlideContent {
  headline?: string;
  statement?: string;
  highlights?: string | string[];
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const IntroStatementSlide2 = {
  id: "intro-statement2-slide",
  name: "Introductory Statement Slide (Ocean Waves)",
  fields: [
    {
      id: "headline",
      label: "Headline or Question",
      type: "text",
    },
    {
      id: "statement",
      label: "Introductory Statement",
      type: "text",
    },
    {
      id: "highlights",
      label: "Key Highlights (comma separated)",
      type: "text",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
    },
  ],
  render: (content: SlideContent) => {
    const {
      headline = "",
      statement = "",
      highlights = "",
      bgColor,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      // Update the configuration in PreviewSlides
      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const highlightArray = Array.isArray(highlights)
      ? highlights
      : highlights
        ? highlights.split(",").map((h: string) => h.trim())
        : [];

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Wave Animation Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30" />
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute bottom-0 left-0 right-0 h-64 opacity-30"
              style={{
                background:
                  "linear-gradient(to bottom, transparent, currentColor)",
                animation: `wave-${i + 1} ${8 + i * 2}s ease-in-out infinite`,
                transform: `translateY(${10 + i * 5}px) scale(1.${i + 1})`,
              }}
            />
          ))}
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-16 py-12">
          <div className="max-w-5xl mx-auto text-center">
            {/* Headline Container */}
            <div className="relative mb-12">
              {/* Decorative Lines */}
              <div className="absolute -inset-4">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              </div>

              {/* Headline */}
              <EditableDiv
                text={headline}
                onEdit={handleEdit("headline")}
                className="text-6xl font-black mb-4 tracking-tight text-white"
                placeholder="Enter headline..."
              />
            </div>

            {/* Main Statement */}
            <div className="relative mb-16">
              <EditableDiv
                text={statement}
                onEdit={handleEdit("statement")}
                className="text-3xl font-light leading-relaxed text-white/90"
                placeholder="Enter statement..."
              />
            </div>

            {/* Highlights Grid */}
            {highlightArray.length > 0 && (
              <div className="grid grid-cols-3 gap-8 mt-8">
                {highlightArray.map((highlight, index) => (
                  <div key={index} className="relative group">
                    <div className="absolute inset-0 bg-white/5 rounded-lg blur-lg transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
                    <div className="relative bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                      {/* Decorative Dot */}
                      <div className="w-2 h-2 bg-white/50 rounded-full mx-auto mb-4" />
                      <EditableDiv
                        text={highlight}
                        onEdit={(value) => {
                          const newHighlights = [...highlightArray];
                          newHighlights[index] = value;
                          handleEdit("highlights")(newHighlights.join(", "));
                        }}
                        className="text-lg font-medium text-white/90"
                        placeholder={`Enter highlight ${index + 1}...`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
};

export default IntroStatementSlide2;
